/**
 * The Welcome step's name save (#1316).
 *
 * `updateUserAttributes({ fullName })` goes to shared-auth's
 * `PATCH /v1/me/profile` (shared-react-auth 0.53.0), which applies the sign-up
 * name rule before it writes. A name the rule refuses comes back as a 400 with
 * a code — `name_required`, `name_too_long`, `name_invalid_characters`,
 * `name_needs_letter` — which the package throws as a `ProfileUpdateError`
 * whose `problem` names the rule and whose `message` is its sentence (never
 * the server's text).
 *
 * That refusal is the person's to correct, so it is said. Anything else — no
 * network, a 503, an ended session — stays best-effort, as it always was: the
 * first session is never blocked on the name.
 */
import { ProfileUpdateError } from '@puglieseweb/shared-react-auth';

export type WelcomeNameOutcome =
  /** Nothing to save: the box is empty, or holds the name already stored. */
  | { kind: 'unchanged' }
  | { kind: 'saved' }
  /** The name breaks the rule; `sentence` says which. Stay and let them fix it. */
  | { kind: 'refused'; sentence: string }
  /** Not about the name (network, 503, 401…): carry on without it. */
  | { kind: 'failed' };

export async function saveWelcomeName(
  update: (attributes: Record<string, string>) => Promise<void>,
  typed: string,
  current: string | undefined,
): Promise<WelcomeNameOutcome> {
  const name = typed.trim();
  if (!name || name === current) return { kind: 'unchanged' };
  try {
    await update({ fullName: name });
    return { kind: 'saved' };
  } catch (err) {
    if (err instanceof ProfileUpdateError && err.problem) {
      return { kind: 'refused', sentence: err.message };
    }
    return { kind: 'failed' };
  }
}
